import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Table, TableBody, TableHead,
  Toolbar, Typography, CircularProgress, TablePagination, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Box, FormControl, InputLabel, Select, InputAdornment, IconButton
} from "@mui/material";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { Delete, Block, CheckCircle, Info, Visibility, Search, FilterList, Warning, VerifiedUser } from "@mui/icons-material";
import {
  getDrivers,
  suspendDriver,
  activateDriver,
  deleteDriver,
  getDriverDetails,
  updateDriver,
  getSignedDriverDocUrl,
  approveDriver
} from "../services/driverService";
import {
  StyledTableContainer,
  StyledTableCell,
  StyledTableRow,
  StyledPaper,
} from "../assets/styles/tableStyle"
import FancyContextMenu, {ContextMenuAction} from "../components/FancyContextMenu";
import Edit from "@mui/icons-material/Edit";
import {DriverStatus} from "../types/enums";
type MuiColor = "success" | "error" | "warning" | "primary" | "info" | undefined;

export enum DocStatus {
  MISSING = "MISSING",
  PENDING = "PENDING",
  VERIFIED = "VERIFIED",
  REJECTED = "REJECTED",
  EXPIRED = "EXPIRED"
}

type Driver = {
  id: number;
  userUid: string;
  licenseNumber?: string;
  vehicleDetails?: string;
  carName?: string;
  vehicleType?: string;
  vehicleColor?: string;
  vehicleRcDetails?: string;
  vehicleLicensePlateNumber?: string;
  isAvailable: boolean;
  rating: number;
  isDeleted: boolean;
  createdAt?: string;
  updatedAt?: string;
  isRCAvailable: boolean;
  isRCVerified: boolean;
  rcStatus?: DocStatus;
  isDLAvailable: boolean;
  isDLVerified: boolean;
  dlStatus?: DocStatus;
  isProfilePhotoAvailable: boolean;
  isProfilePhotoVerified: boolean;
  mandatoryPermissions?: any;
  isTermsAndConditionAccepted: boolean;
  cImageUrl?: string | string[];
  dlImageUrl?: string | string[];
  profilePhotoUrl?: string;
  status: DriverStatus;
  insuranceNumber?: string;
  insuranceUrls?: string[];
  insuranceStatus?: DocStatus;
  pucNumber?: string;
  pucUrls?: string[];
  pucStatus?: DocStatus;
  statePermitNumber?: string;
  statePermitUrls?: string[];
  statePermitStatus?: DocStatus;
  aitpNumber?: string;
  aitpUrls?: string[];
  aitpStatus?: DocStatus;
};

const booleanFields = [
  "isAvailable", "isDeleted", "isRCAvailable", "isRCVerified",
  "isDLAvailable", "isDLVerified", "isProfilePhotoAvailable", "isProfilePhotoVerified", "isTermsAndConditionAccepted"
] as const;

const allFields = [
  "licenseNumber", "vehicleDetails", "carName", "vehicleType", "vehicleColor", "vehicleRcDetails",
  "vehicleLicensePlateNumber", "rating", "mandatoryPermissions", "cImageUrl", "dlImageUrl", "profilePhotoUrl",
  "status",
  "insuranceNumber", "pucNumber", "statePermitNumber", "aitpNumber",
  ...booleanFields
] as const;

// --- Helper for Status Dropdown ---
const DocStatusSelect: React.FC<{ label: string; value: DocStatus; onChange: (val: DocStatus) => void }> = ({ label, value, onChange }) => (
    <TextField
        select
        label={label}
        value={value || DocStatus.MISSING}
        onChange={(e) => onChange(e.target.value as DocStatus)}
        fullWidth
        size="small"
    >
      {Object.values(DocStatus).map((status) => (
          <MenuItem key={status} value={status}>
            {status}
          </MenuItem>
      ))}
    </TextField>
);

// --- Updated Image Preview to handle Slider (Horizontal Scroll) ---
const ImagePreviewBox: React.FC<{ imageUrls?: string[], altText?: string }> = ({ imageUrls = [], altText }) => {
  const [open, setOpen] = useState(false);
  const [activeImage, setActiveImage] = useState<string>("");

  const handleOpen = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    setActiveImage(url);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  // Filter out empty strings just in case
  const validUrls = imageUrls.filter(u => !!u);

  return (
      <>
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'nowrap', // Force single line
          overflowX: 'auto',  // Enable horizontal scrolling
          width: '100%',
          maxWidth: 200,      // Constrain width in grid
          pb: 1,              // Padding for scrollbar
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: 4 }
        }}>
          {validUrls.length > 0 ? (
              validUrls.slice(0, 5).map((url, index) => (
                  <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        flexShrink: 0, // Prevent shrinking
                        height: 70,
                        width: 90,
                        bgcolor: '#f2f2f2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2,
                        boxShadow: 1,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        '&:hover .lens-icon': { opacity: 1 },
                      }}
                      onClick={(e) => handleOpen(e, url)}
                  >
                    <img
                        src={url}
                        alt={`${altText} ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                    />
                    <Box
                        className="lens-icon"
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          bgcolor: 'rgba(255,255,255,0.8)',
                          borderRadius: '50%',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          p: 0.2,
                        }}
                    >
                      <ZoomInIcon style={{ fontSize: 16 }} color="action" />
                    </Box>
                  </Box>
              ))
          ) : (
              <Box
                  sx={{
                    height: 70,
                    width: 90,
                    bgcolor: '#f2f2f2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    border: '1px dashed #ccc'
                  }}
              >
                <Typography color="textSecondary" fontSize="0.7rem">
                  No Image
                </Typography>
              </Box>
          )}
        </Box>
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            disableEnforceFocus
        >
          <Box sx={{
            p: 2,
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}>
            <IconButton
                onClick={handleClose}
                sx={{ position: 'absolute', top: 5, right: 5, bgcolor: '#fff' }}
            >
              <Block fontSize="small"/>
            </IconButton>
            {activeImage && (
                <img
                    src={activeImage}
                    alt={altText}
                    style={{ maxHeight: '85vh', maxWidth: '90vw', borderRadius: 8 }}
                />
            )}
          </Box>
        </Dialog>
      </>
  );
};

const getStatusColor = (status: DriverStatus): MuiColor => {
  switch (status) {
    case DriverStatus.ONLINE:
      return "success";
    case DriverStatus.SILENT:
      return "warning";
    case DriverStatus.OFFLINE:
      return "error";
    default:
      return "info";
  }
};

const DriverPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [filteredDrivers, setFilteredDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // Filters State
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || "");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [availabilityFilter, setAvailabilityFilter] = useState<string>("all");

  // Modal State
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [editData, setEditData] = useState<Partial<Driver>>({});
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  // Two separate modals
  const [verifiedModalOpen, setVerifiedModalOpen] = useState(false);
  const [complianceModalOpen, setComplianceModalOpen] = useState(false);

  const [verifiedForm, setVerifiedForm] = useState<{
    id: number | null;
    licenseNumber: string;
    vehicleLicensePlateNumber: string;
    isProfilePhotoVerified: boolean;
    isAvailable: boolean;
    rcStatus: DocStatus;
    dlStatus: DocStatus;
    cImageUrl: string[];
    dlImageUrl: string[];
    profilePhotoUrl: string[];
    insuranceNumber: string;
    insuranceStatus: DocStatus;
    insuranceUrls: string[];
    pucNumber: string;
    pucStatus: DocStatus;
    pucUrls: string[];
    statePermitNumber: string;
    statePermitStatus: DocStatus;
    statePermitUrls: string[];
    aitpNumber: string;
    aitpStatus: DocStatus;
    aitpUrls: string[];
  }>({
    id: null,
    licenseNumber: "",
    vehicleLicensePlateNumber: "",
    isProfilePhotoVerified: false,
    isAvailable: false,
    rcStatus: DocStatus.MISSING,
    dlStatus: DocStatus.MISSING,
    cImageUrl: [],
    dlImageUrl: [],
    profilePhotoUrl: [],
    insuranceNumber: "",
    insuranceStatus: DocStatus.MISSING,
    insuranceUrls: [],
    pucNumber: "",
    pucStatus: DocStatus.MISSING,
    pucUrls: [],
    statePermitNumber: "",
    statePermitStatus: DocStatus.MISSING,
    statePermitUrls: [],
    aitpNumber: "",
    aitpStatus: DocStatus.MISSING,
    aitpUrls: []
  });

  const handleContextMenu = (event: React.MouseEvent, row: any) => {
    event.preventDefault();
    setSelectedRow(row);
    setMenu({ top: event.clientY, left: event.clientX });
  };
  const handleCloseMenu = () => setMenu(null);

  // --- MENU LOGIC ---
  const isMandatoryVerified =
      selectedRow?.isRCVerified &&
      selectedRow?.isDLVerified &&
      selectedRow?.isProfilePhotoVerified;

  const complianceDocs = [
    selectedRow?.insuranceStatus,
    selectedRow?.pucStatus,
    selectedRow?.statePermitStatus,
    selectedRow?.aitpStatus
  ];

  // Logic:
  // 1. All Verified -> "Compliance Verified"
  // 2. Not all verified, but ANY data exists (Pending, Rejected, Expired, or even Mixed Verified) -> "Review Compliance"
  // 3. All Missing -> "Compliance Docs"
  const isComplianceAllVerified = complianceDocs.every(s => s === DocStatus.VERIFIED);
  const hasSomeComplianceData = complianceDocs.some(s =>
      s === DocStatus.PENDING ||
      s === DocStatus.REJECTED ||
      s === DocStatus.EXPIRED ||
      s === DocStatus.VERIFIED
  );

  const mandatoryMenuConfig = isMandatoryVerified
      ? { text: "Driver Verified", iconColor: "success" as MuiColor, Icon: CheckCircle }
      : { text: "Approve Driver", iconColor: "warning" as MuiColor, Icon: Warning };

  const complianceMenuConfig = isComplianceAllVerified
      ? { text: "Compliance Verified", iconColor: "success" as MuiColor, Icon: VerifiedUser }
      : hasSomeComplianceData
          ? { text: "Review Pending Compliance", iconColor: "error" as MuiColor, Icon: Warning }
          : { text: "Compliance Docs", iconColor: "info" as MuiColor, Icon: Info };

  const actions: ContextMenuAction[] = [
    {
      dividerBefore: true,
      label: "View Full Details",
      icon: <Visibility color="primary" fontSize="small" />,
      onClick: () => {
        if (selectedRow) navigate(`/driver/${selectedRow.id}`);
      },
      color: "primary",
    },
    {
      label: "Edit / Quick View",
      icon: <Edit color="info" fontSize="small" />,
      onClick: () => handleRowClick(selectedRow.id),
      color: "info",
    },
    {
      label: selectedRow?.isAvailable ? "Mark UnAvailable" : "Mark Available",
      icon: selectedRow?.isAvailable
          ? <Block color="error" fontSize="small" />
          : <CheckCircle color="success" fontSize="small" />,
      onClick: () => selectedRow?.isAvailable
          ? handleSuspend(selectedRow.id)
          : handleActivate(selectedRow.id),
      color: selectedRow?.isAvailable ? "warning" : "success",
    },
    {
      dividerBefore: true,
      label: mandatoryMenuConfig.text,
      icon: <mandatoryMenuConfig.Icon color={mandatoryMenuConfig.iconColor} fontSize="small" />,
      onClick: () => handleOpenDriverVerified(selectedRow, 'MANDATORY'),
      color: mandatoryMenuConfig.iconColor,
    },
    {
      label: complianceMenuConfig.text,
      icon: <complianceMenuConfig.Icon color={complianceMenuConfig.iconColor} fontSize="small" />,
      onClick: () => handleOpenDriverVerified(selectedRow, 'COMPLIANCE'),
      color: complianceMenuConfig.iconColor,
    },
    {
      dividerBefore: true,
      label: "Show Rides",
      icon: <Info color="info" fontSize="small" />,
      onClick: () => {
        if (!selectedRow?.userUid) return;
        handleShowRides(selectedRow);
      },
      color: "info",
    },
    {
      label: "Show Wallet",
      icon: <Info color="info" fontSize="small" />,
      onClick: () => {
        if (!selectedRow?.userUid) return;
        handleShowWalletDetails(selectedRow);
      },
      color: "info",
    },
    {
      dividerBefore: true,
      label: "Delete",
      icon: <Delete color="error" fontSize="small" />,
      onClick: () => handleDelete(selectedRow.id),
      color: "error",
    },
  ];

  useEffect(() => {
    fetchDrivers();
  }, []);

  // Filtering Logic
  useEffect(() => {
    let result = drivers;

    // 1. Search Filter
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(d =>
          d.userUid.toLowerCase().includes(lowerTerm) ||
          d.licenseNumber?.toLowerCase().includes(lowerTerm) ||
          d.vehicleLicensePlateNumber?.toLowerCase().includes(lowerTerm) ||
          String(d.id).includes(lowerTerm)
      );
    }

    // 2. Status Filter
    if (statusFilter !== "all") {
      result = result.filter(d => d.status === statusFilter);
    }

    // 3. Availability Filter
    if (availabilityFilter !== "all") {
      const isAvailable = availabilityFilter === "available";
      result = result.filter(d => d.isAvailable === isAvailable);
    }

    setFilteredDrivers(result);
    setPage(0); // Reset page on filter change
  }, [drivers, searchTerm, statusFilter, availabilityFilter]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const res = await getDrivers();
      setDrivers(res.data);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (id: number) => {
    setSelectedId(id);
    setDetailOpen(true);
    setEditLoading(true);
    try {
      const res = await getDriverDetails(id);
      setSelectedDriver(res.data);
      setEditData(res.data);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedId(null);
    setSelectedDriver(null);
    setEditData({});
  };

  const handleFieldChange = (field: keyof Driver, value: string | boolean | DriverStatus) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedId) return;
    setEditLoading(true);
    try {
      const updateFields: any = {};
      const editableFields = allFields.filter(f => f !== 'status');
      editableFields.forEach((field) => {
        if (editData[field] !== undefined) updateFields[field] = editData[field];
      });
      await updateDriver(selectedId, updateFields);
      await fetchDrivers();
      setDetailOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenDriverVerified = async (driver: Driver, type: 'MANDATORY' | 'COMPLIANCE') => {
    // --- Fetch fresh details to ensure deep arrays exist ---
    let fullDriver = driver;
    try {
      const res = await getDriverDetails(driver.id);
      if(res.data) fullDriver = res.data;
    } catch(e) {
      console.error("Could not fetch latest details, using list data");
    }

    const toArray = (val: string | string[] | undefined) => {
      if (!val) return [];
      return Array.isArray(val) ? val : [val];
    };

    // Prepare arrays for signing
    const cKeys = toArray(fullDriver.cImageUrl);
    const dlKeys = toArray(fullDriver.dlImageUrl);
    const pKeys = toArray(fullDriver.profilePhotoUrl);
    const insKeys = toArray(fullDriver.insuranceUrls);
    const pucKeys = toArray(fullDriver.pucUrls);
    const permKeys = toArray(fullDriver.statePermitUrls);
    const aitpKeys = toArray(fullDriver.aitpUrls);

    // Fetch all signed URLs in parallel
    const [cUrls, dlUrls, profileUrls, insUrls, pucUrls, permUrls, aitpUrls] = await Promise.all([
      Promise.all(cKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(dlKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(pKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(insKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(pucKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(permKeys.map(k => getSignedDriverDocUrl(k))),
      Promise.all(aitpKeys.map(k => getSignedDriverDocUrl(k))),
    ]);

    setVerifiedForm({
      id: fullDriver.id,
      licenseNumber: fullDriver.licenseNumber || "",
      vehicleLicensePlateNumber: fullDriver.vehicleLicensePlateNumber || "",

      // Mandatory Booleans
      isProfilePhotoVerified: fullDriver.isProfilePhotoVerified,
      isAvailable: fullDriver.isAvailable,

      // Mandatory Enum Statuses (Fallback to Verified/Missing if null)
      rcStatus: fullDriver.rcStatus || (fullDriver.isRCVerified ? DocStatus.VERIFIED : DocStatus.MISSING),
      dlStatus: fullDriver.dlStatus || (fullDriver.isDLVerified ? DocStatus.VERIFIED : DocStatus.MISSING),

      cImageUrl: cUrls,
      dlImageUrl: dlUrls,
      profilePhotoUrl: profileUrls,

      // Compliance
      insuranceNumber: fullDriver.insuranceNumber || "",
      insuranceStatus: fullDriver.insuranceStatus || DocStatus.MISSING,
      insuranceUrls: insUrls,
      pucNumber: fullDriver.pucNumber || "",
      pucStatus: fullDriver.pucStatus || DocStatus.MISSING,
      pucUrls: pucUrls,
      statePermitNumber: fullDriver.statePermitNumber || "",
      statePermitStatus: fullDriver.statePermitStatus || DocStatus.MISSING,
      statePermitUrls: permUrls,
      aitpNumber: fullDriver.aitpNumber || "",
      aitpStatus: fullDriver.aitpStatus || DocStatus.MISSING,
      aitpUrls: aitpUrls,
    });

    if(type === 'MANDATORY') setVerifiedModalOpen(true);
    else setComplianceModalOpen(true);
  };

  const handleSuspend = async (id: number) => {
    setLoading(true);
    try {
      await suspendDriver(id);
      await fetchDrivers();
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (id: number) => {
    setLoading(true);
    try {
      await activateDriver(id);
      await fetchDrivers();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      await deleteDriver(id);
      await fetchDrivers();
    } finally {
      setLoading(false);
    }
  };

  const handleShowRides = (row: any) => {
    if (!row?.userUid) return;
    const query = `?driverUid=${encodeURIComponent(row.userUid)}`;
    window.location.href = `/rides${query}`;
  };

  const handleShowWalletDetails = (row: any) => {
    if (!row?.userUid) return;
    const query = `?driverUid=${encodeURIComponent(row.userUid)}`;
    window.location.href = `/wallet-transactions${query}`;
  };

  const paginatedDrivers = filteredDrivers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  return (
      <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
        <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6">Driver Management</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
                placeholder="Search UID, License, Plate..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  startAdornment={<FilterList fontSize="small" sx={{ mr: 1, color: 'action.active' }} />}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value={DriverStatus.ONLINE}>Online</MenuItem>
                <MenuItem value={DriverStatus.OFFLINE}>Offline</MenuItem>
                <MenuItem value={DriverStatus.SILENT}>Silent</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Availability</InputLabel>
              <Select
                  value={availabilityFilter}
                  label="Availability"
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </Select>
            </FormControl>
            {(searchTerm || statusFilter !== 'all' || availabilityFilter !== 'all') && (
                <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("all");
                      setAvailabilityFilter("all");
                    }}
                >
                  Clear
                </Button>
            )}
          </Box>
        </Toolbar>
        {loading ? (
            <CircularProgress sx={{ margin: 2 }} />
        ) : (
            <StyledTableContainer>
              <Table sx={{ minWidth: 1200 }}>
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>ID</StyledTableCell>
                    <StyledTableCell>User UID</StyledTableCell>
                    <StyledTableCell>License Number</StyledTableCell>
                    <StyledTableCell>Vehicle Plate</StyledTableCell>
                    <StyledTableCell>Available</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Rating</StyledTableCell>
                    <StyledTableCell>Deleted</StyledTableCell>
                    <StyledTableCell>Created At</StyledTableCell>
                    <StyledTableCell>Updated At</StyledTableCell>
                    <StyledTableCell>RC Verified</StyledTableCell>
                    <StyledTableCell>DL Verified</StyledTableCell>
                    <StyledTableCell>Profile Verified</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {paginatedDrivers.map((driver) => (
                      <StyledTableRow
                          key={driver.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onContextMenu={e => handleContextMenu(e, driver)}
                          onClick={() => handleRowClick(driver.id)}
                      >
                        <StyledTableCell>{driver.id}</StyledTableCell>
                        <StyledTableCell>{driver.userUid}</StyledTableCell>
                        <StyledTableCell>{driver.licenseNumber || "-"}</StyledTableCell>
                        <StyledTableCell>{driver.vehicleLicensePlateNumber || "-"}</StyledTableCell>
                        <StyledTableCell>
                          {driver.isAvailable
                              ? <CheckCircle color="success" fontSize="small" />
                              : <Block color="error" fontSize="small" />}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Chip
                              label={driver.status || "N/A"}
                              color={getStatusColor(driver.status)}
                              size="small"
                          />
                        </StyledTableCell>
                        <StyledTableCell>{driver.rating?.toFixed(1) ?? "-"}</StyledTableCell>
                        <StyledTableCell>
                          {driver.isDeleted
                              ? <Chip label="Yes" color="error" />
                              : <Chip label="No" color="success" />}
                        </StyledTableCell>
                        <StyledTableCell>{driver.createdAt ? driver.createdAt.slice(0, 10) : "-"}</StyledTableCell>
                        <StyledTableCell>{driver.updatedAt ? driver.updatedAt.slice(0, 10) : "-"}</StyledTableCell>
                        <StyledTableCell>
                          {driver.isRCVerified
                              ? <CheckCircle color="success" fontSize="small" />
                              : <Block color="error" fontSize="small" />}
                        </StyledTableCell>
                        <StyledTableCell>
                          {driver.isDLVerified
                              ? <CheckCircle color="success" fontSize="small" />
                              : <Block color="error" fontSize="small" />}
                        </StyledTableCell>
                        <StyledTableCell>
                          {driver.isProfilePhotoVerified
                              ? <CheckCircle color="success" fontSize="small" />
                              : <Block color="error" fontSize="small" />}
                        </StyledTableCell>
                      </StyledTableRow>
                  ))}
                  {paginatedDrivers.length === 0 && !loading && (
                      <StyledTableRow>
                        <StyledTableCell colSpan={16} align="center">
                          <Typography color="textSecondary" sx={{ py: 3 }}>No drivers found matching filters.</Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={filteredDrivers.length} // Use filtered length
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
              />
            </StyledTableContainer>
        )}

        {/* Full Driver Details Modal */}
        <Dialog
            open={detailOpen}
            onClose={handleCloseDetail}
            fullWidth
            maxWidth="md"
            disableEnforceFocus
        >
          <DialogTitle>Driver Details</DialogTitle>
          <DialogContent>
            {editLoading ? (
                <CircularProgress />
            ) : selectedDriver && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<Visibility />}
                        onClick={() => navigate(`/driver/${selectedDriver.id}`)}
                    >
                      Go to Full Details Page
                    </Button>
                  </Box>

                  <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 2,
                        mt: 1,
                      }}
                  >
                    <TextField label="ID" value={selectedDriver.id} fullWidth disabled />
                    <TextField label="User UID" value={selectedDriver.userUid} fullWidth disabled />
                    <TextField label="Created At" value={selectedDriver.createdAt || ""} fullWidth disabled />
                    <TextField label="Updated At" value={selectedDriver.updatedAt || ""} fullWidth disabled />
                    <TextField
                        key="status"
                        label="Status"
                        value={editData.status ?? DriverStatus.OFFLINE}
                        fullWidth
                        disabled
                    />
                    {allFields.filter(f => !booleanFields.includes(f as any) && f !== 'status' && !['cImageUrl', 'dlImageUrl', 'profilePhotoUrl', 'mandatoryPermissions', 'insuranceUrls', 'pucUrls', 'statePermitUrls', 'aitpUrls'].includes(f)).map(field => (
                        <TextField
                            key={field}
                            label={field.charAt(0).toUpperCase() + field.slice(1)}
                            value={editData[field as keyof Driver] ?? ""}
                            fullWidth
                            onChange={e => handleFieldChange(field as keyof Driver, e.target.value)}
                        />
                    ))}
                    {booleanFields.map(field => (
                        <TextField
                            select
                            key={field}
                            label={field.charAt(0).toUpperCase() + field.slice(1)}
                            value={editData[field as keyof Driver] ? "yes" : "no"}
                            fullWidth
                            onChange={e =>
                                handleFieldChange(
                                    field as keyof Driver,
                                    e.target.value === "yes"
                                )
                            }
                        >
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </TextField>
                    ))}
                  </Box>
                </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetail}>Close</Button>
            <Button
                variant="contained"
                onClick={handleSaveEdit}
                disabled={editLoading}
            >Save</Button>
          </DialogActions>
        </Dialog>

        {/* --- MODAL 1: MANDATORY DOCS (RC, DL, Profile) --- */}
        <Dialog
            open={verifiedModalOpen}
            onClose={() => setVerifiedModalOpen(false)}
            fullWidth
            maxWidth="md"
            disableEnforceFocus
        >
          <DialogTitle>Approve Driver (Mandatory Docs)</DialogTitle>
          <DialogContent>
            <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 3,
                  alignItems: 'center',
                  py: 2,
                }}
            >
              {/* Row 1: RC (Restricted to VERIFIED / REJECTED) */}
              <TextField
                  label="License Plate"
                  value={verifiedForm.vehicleLicensePlateNumber}
                  fullWidth
                  disabled
              />
              <ImagePreviewBox imageUrls={verifiedForm.cImageUrl} altText="RC" />
              <TextField
                  select
                  label="RC Status"
                  value={verifiedForm.rcStatus === DocStatus.VERIFIED ? DocStatus.VERIFIED : (verifiedForm.rcStatus === DocStatus.REJECTED ? DocStatus.REJECTED : verifiedForm.rcStatus)}
                  onChange={(e) => setVerifiedForm(prev => ({ ...prev, rcStatus: e.target.value as DocStatus }))}
                  fullWidth
                  size="small"
              >
                <MenuItem value="" disabled><em>Select Status</em></MenuItem>
                <MenuItem value={DocStatus.VERIFIED}>VERIFIED</MenuItem>
                <MenuItem value={DocStatus.REJECTED}>REJECTED</MenuItem>
                <MenuItem value={DocStatus.PENDING}>PENDING</MenuItem>
                <MenuItem value={DocStatus.MISSING}>MISSING</MenuItem>
                <MenuItem value={DocStatus.EXPIRED}>EXPIRED</MenuItem>
              </TextField>

              {/* Row 2: DL (Restricted to VERIFIED / REJECTED) */}
              <TextField
                  label="License Number"
                  value={verifiedForm.licenseNumber}
                  fullWidth
                  disabled
              />
              <ImagePreviewBox imageUrls={verifiedForm.dlImageUrl} altText="DL" />
              <TextField
                  select
                  label="DL Status"
                  value={verifiedForm.dlStatus === DocStatus.VERIFIED ? DocStatus.VERIFIED : (verifiedForm.dlStatus === DocStatus.REJECTED ? DocStatus.REJECTED : verifiedForm.dlStatus)}
                  onChange={(e) => setVerifiedForm(prev => ({ ...prev, dlStatus: e.target.value as DocStatus }))}
                  fullWidth
                  size="small"
              >
                <MenuItem value="" disabled><em>Select Status</em></MenuItem>
                <MenuItem value={DocStatus.VERIFIED}>VERIFIED</MenuItem>
                <MenuItem value={DocStatus.REJECTED}>REJECTED</MenuItem>
                <MenuItem value={DocStatus.PENDING}>PENDING</MenuItem>
                <MenuItem value={DocStatus.MISSING}>MISSING</MenuItem>
                <MenuItem value={DocStatus.EXPIRED}>EXPIRED</MenuItem>
              </TextField>

              {/* Row 3: Profile (Restricted to VERIFIED / REJECTED) */}
              <TextField
                  select
                  label="Driver Available"
                  value={verifiedForm.isAvailable ? "yes" : "no"}
                  onChange={e => setVerifiedForm(prev => ({ ...prev, isAvailable: e.target.value === "yes" }))}
                  fullWidth
                  size="small"
              >
                <MenuItem value="yes">Available</MenuItem>
                <MenuItem value="no">Unavailable</MenuItem>
              </TextField>
              <ImagePreviewBox imageUrls={verifiedForm.profilePhotoUrl} altText="Profile" />
              <TextField
                  select
                  label="Photo Status"
                  value={verifiedForm.isProfilePhotoVerified === true ? "VERIFIED" : (verifiedForm.isProfilePhotoVerified === false ? "PENDING" : "")}
                  onChange={e => setVerifiedForm(prev => ({ ...prev, isProfilePhotoVerified: e.target.value === "VERIFIED" }))}
                  fullWidth
                  size="small"
              >
                <MenuItem value="" disabled><em>Select Status</em></MenuItem>
                <MenuItem value="VERIFIED">VERIFIED</MenuItem>
                <MenuItem value="PENDING">PENDING</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVerifiedModalOpen(false)}>
              Cancel
            </Button>
            <Button
                variant="contained"
                onClick={async () => {
                  if (verifiedForm.id == null) return;
                  // We manually sync the booleans for the backend logic
                  await approveDriver(verifiedForm.id, {
                    // Send Enum Statuses
                    rcStatus: verifiedForm.rcStatus,
                    dlStatus: verifiedForm.dlStatus,

                    // Derive Booleans (Backend requires these for final approval logic)
                    isRCVerified: verifiedForm.rcStatus === DocStatus.VERIFIED,
                    isDLVerified: verifiedForm.dlStatus === DocStatus.VERIFIED,

                    isProfilePhotoVerified: verifiedForm.isProfilePhotoVerified,
                    isAvailable: verifiedForm.isAvailable,
                  });
                  setVerifiedModalOpen(false);
                  await fetchDrivers();
                }}
            >
              Approve Driver
            </Button>
          </DialogActions>
        </Dialog>

        {/* --- MODAL 2: COMPLIANCE DOCS (Insurance, PUC, Permit) --- */}
        <Dialog
            open={complianceModalOpen}
            onClose={() => setComplianceModalOpen(false)}
            fullWidth
            maxWidth="md"
            disableEnforceFocus
        >
          <DialogTitle>Verify Compliance Documents</DialogTitle>
          <DialogContent>
            <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 3,
                  alignItems: 'center',
                  py: 2,
                }}
            >
              {/* Row 4: Insurance */}
              <TextField
                  label="Insurance No."
                  value={verifiedForm.insuranceNumber || ""}
                  onChange={e => setVerifiedForm(prev => ({ ...prev, insuranceNumber: e.target.value }))}
                  fullWidth
                  size="small"
              />
              <ImagePreviewBox imageUrls={verifiedForm.insuranceUrls} altText="Insurance" />
              <DocStatusSelect
                  label="Insurance Status"
                  value={verifiedForm.insuranceStatus}
                  onChange={(val) => setVerifiedForm(prev => ({ ...prev, insuranceStatus: val }))}
              />

              {/* Row 5: PUC */}
              <TextField
                  label="PUC Number"
                  value={verifiedForm.pucNumber || ""}
                  onChange={e => setVerifiedForm(prev => ({ ...prev, pucNumber: e.target.value }))}
                  fullWidth
                  size="small"
              />
              <ImagePreviewBox imageUrls={verifiedForm.pucUrls} altText="PUC" />
              <DocStatusSelect
                  label="PUC Status"
                  value={verifiedForm.pucStatus}
                  onChange={(val) => setVerifiedForm(prev => ({ ...prev, pucStatus: val }))}
              />

              {/* Row 6: State Permit */}
              <TextField
                  label="Permit No."
                  value={verifiedForm.statePermitNumber || ""}
                  onChange={e => setVerifiedForm(prev => ({ ...prev, statePermitNumber: e.target.value }))}
                  fullWidth
                  size="small"
              />
              <ImagePreviewBox imageUrls={verifiedForm.statePermitUrls} altText="Permit" />
              <DocStatusSelect
                  label="Permit Status"
                  value={verifiedForm.statePermitStatus}
                  onChange={(val) => setVerifiedForm(prev => ({ ...prev, statePermitStatus: val }))}
              />

              {/* Row 7: AITP */}
              <TextField
                  label="AITP Number"
                  value={verifiedForm.aitpNumber || ""}
                  onChange={e => setVerifiedForm(prev => ({ ...prev, aitpNumber: e.target.value }))}
                  fullWidth
                  size="small"
              />
              <ImagePreviewBox imageUrls={verifiedForm.aitpUrls} altText="AITP" />
              <DocStatusSelect
                  label="AITP Status"
                  value={verifiedForm.aitpStatus}
                  onChange={(val) => setVerifiedForm(prev => ({ ...prev, aitpStatus: val }))}
              />

            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setComplianceModalOpen(false)}>
              Cancel
            </Button>
            <Button
                variant="contained"
                onClick={async () => {
                  if (verifiedForm.id == null) return;
                  await approveDriver(verifiedForm.id, {
                    // Save Statuses
                    insuranceStatus: verifiedForm.insuranceStatus,
                    pucStatus: verifiedForm.pucStatus,
                    statePermitStatus: verifiedForm.statePermitStatus,
                    aitpStatus: verifiedForm.aitpStatus,
                    // Save Editable Numbers
                    insuranceNumber: verifiedForm.insuranceNumber,
                    pucNumber: verifiedForm.pucNumber,
                    statePermitNumber: verifiedForm.statePermitNumber,
                    aitpNumber: verifiedForm.aitpNumber,
                  });
                  setComplianceModalOpen(false);
                  await fetchDrivers();
                }}
            >
              Save Compliance
            </Button>
          </DialogActions>
        </Dialog>

        <FancyContextMenu
            actions={actions}
            open={Boolean(menu)}
            onClose={handleCloseMenu}
            anchorPosition={menu}
            title={selectedRow?.name || selectedRow?.userUid || "Row Actions"}
        />
      </StyledPaper>
  );
};

export default DriverPage;